import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, API } = useAuth();
  const [status, setStatus] = useState('checking');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const maxAttempts = 5;
  const pollInterval = 2000; // 2 seconds

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found');
      setStatus('error');
      return;
    }

    if (!token) {
      navigate('/login');
      return;
    }

    // Start polling for payment status
    pollPaymentStatus();
  }, []);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/payment/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'paid') {
        setStatus('success');
        // Redirect to settling-in page after 2 seconds
        setTimeout(() => {
          navigate('/settling-in');
        }, 2000);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError(err.response?.data?.detail || 'Failed to verify payment');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#E8E4DF] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-2xl p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-800 mx-auto mb-4"></div>
            <h2 
              className="text-2xl mb-2 text-gray-800"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Verifying Payment...
            </h2>
            <p 
              className="text-gray-600"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Please wait while we confirm your payment
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="text-green-600 text-6xl mb-4">✓</div>
            <h2 
              className="text-2xl mb-2 text-gray-800"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Payment Successful!
            </h2>
            <p 
              className="text-gray-600 mb-4"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Your journaling journey is ready. Redirecting...
            </p>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="text-yellow-600 text-6xl mb-4">⏱</div>
            <h2 
              className="text-2xl mb-2 text-gray-800"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Payment Check Timed Out
            </h2>
            <p 
              className="text-gray-600 mb-4"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              We're still processing your payment. Please check your email for confirmation.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-800 text-white px-6 py-2 rounded-full hover:bg-gray-700 transition-all"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Return Home
            </button>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="text-red-600 text-6xl mb-4">✕</div>
            <h2 
              className="text-2xl mb-2 text-gray-800"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Payment Session Expired
            </h2>
            <p 
              className="text-gray-600 mb-4"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Your payment session has expired. Please try again.
            </p>
            <button
              onClick={() => navigate('/payment')}
              className="bg-gray-800 text-white px-6 py-2 rounded-full hover:bg-gray-700 transition-all"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Try Again
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="text-red-600 text-6xl mb-4">✕</div>
            <h2 
              className="text-2xl mb-2 text-gray-800"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Verification Error
            </h2>
            <p 
              className="text-gray-600 mb-4"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              {error || 'Unable to verify payment status'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-800 text-white px-6 py-2 rounded-full hover:bg-gray-700 transition-all"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Return Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
